import { Languages } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Button } from '@/components/ui/button';
import { isFeatureActive } from '@/lib/featureFlags';
import { type ConfigData, FeatureFlag } from '@/types/app';

interface AppContextType {
	config?: ConfigData;
}

const STORAGE_KEY = 'quotr-language';

const LANGUAGES = [
	{ code: 'nl', name: 'Nederlands' },
	{ code: 'en', name: 'English' }
];

/**
 * Language switcher component that allows users to change the application language
 *
 * Supported languages:
 * - nl (Dutch)
 * - en (English)
 *
 * The selected language is stored in localStorage and persists across sessions.
 *
 * Only displays when the enable_multi_language feature flag is enabled.
 * When the feature flag is disabled, Dutch is always used (no language switching possible).
 *
 * @example
 * ```tsx
 * <LanguageSwitcher />
 * ```
 */
export default function LanguageSwitcher() {
	const { i18n } = useTranslation();
	const context = useAppContext() as AppContextType | null;
	const config = context?.config;

	const multiLanguageEnabled = isFeatureActive(config, FeatureFlag.ENABLE_MULTI_LANGUAGE);

	// Load saved language preference on mount (only if feature is enabled)
	useEffect(() => {
		if (multiLanguageEnabled) {
			const savedLanguage = localStorage.getItem(STORAGE_KEY);
			if (savedLanguage && LANGUAGES.some((lang) => lang.code === savedLanguage)) {
				i18n.changeLanguage(savedLanguage);
			}
		}
	}, [multiLanguageEnabled, i18n]);

	const changeLanguage = (lng: string) => {
		i18n.changeLanguage(lng);
		localStorage.setItem(STORAGE_KEY, lng);
	};

	// Hide only once we know the feature is disabled; while the config is still
	// loading (flags unavailable) keep showing the switcher as before.
	if (!isFeatureActive(config, FeatureFlag.ENABLE_MULTI_LANGUAGE, true)) {
		return null;
	}

	const nextLanguage = LANGUAGES.find((lang) => lang.code !== i18n.language) || LANGUAGES[1];

	const handleToggleLanguage = () => {
		changeLanguage(nextLanguage.code);
	};

	return (
		<Button
			type="button"
			variant="default"
			size="sm"
			className="min-w-[80px]"
			onClick={handleToggleLanguage}
			icon={<Languages className="w-4 h-4 opacity-75" />}
			aria-label={`Switch to ${nextLanguage.name}`}
			data-testid="language-switcher"
		>
			{nextLanguage.name}
		</Button>
	);
}
